import type { FormFolder } from '@jfh/intake-shared';

/** A form or folder being moved (by drag and drop or "Move to…"). */
export interface MoveItem {
  kind: 'form' | 'folder';
  id: string;
  name: string;
  /** Where it lives now (null = top level). */
  parentId: string | null;
}

export const TOP_LEVEL_LABEL = 'All forms';

export function byId(folders: FormFolder[]): Map<string, FormFolder> {
  return new Map(folders.map((f) => [f.id, f] as const));
}

/** Root-first chain of folders down to `id` (empty for top level). */
export function pathTo(index: Map<string, FormFolder>, id: string | null): FormFolder[] {
  const out: FormFolder[] = [];
  let cur = id ? index.get(id) : undefined;
  while (cur && out.length <= index.size) {
    out.unshift(cur);
    cur = cur.parentId ? index.get(cur.parentId) : undefined;
  }
  return out;
}

/** "Customer accounts › Trade" — or the top-level label. */
export function pathLabel(index: Map<string, FormFolder>, id: string | null): string {
  const chain = pathTo(index, id);
  return chain.length ? chain.map((f) => f.name).join(' › ') : TOP_LEVEL_LABEL;
}

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base', numeric: true });

export function childrenOf(folders: FormFolder[], parentId: string | null): FormFolder[] {
  return folders.filter((f) => f.parentId === parentId).sort(byName);
}

/** True when `candidate` is folder `id` or anywhere beneath it. */
export function isSelfOrDescendant(
  index: Map<string, FormFolder>,
  id: string,
  candidate: string | null,
): boolean {
  return pathTo(index, candidate).some((f) => f.id === id);
}

/** Can `item` be dropped into `target`? (The server re-checks every rule.) */
export function canMoveTo(
  index: Map<string, FormFolder>,
  item: MoveItem,
  target: string | null,
): boolean {
  if (item.parentId === target) return false;
  if (item.kind === 'folder' && isSelfOrDescendant(index, item.id, target)) return false;
  return true;
}

/** Depth-first tree, for the "Move to…" picker. */
export function flattenTree(
  folders: FormFolder[],
  parentId: string | null = null,
  depth = 0,
): { folder: FormFolder; depth: number }[] {
  return childrenOf(folders, parentId).flatMap((folder) => [
    { folder, depth },
    ...flattenTree(folders, folder.id, depth + 1),
  ]);
}

/** Drag payload type — only our own items are accepted as drops. */
export const DRAG_TYPE = 'application/x-jfh-forms-item';
